import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, CreditCard, Calendar, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 }
  }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }
};

export default function Memberships() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingMembership, setEditingMembership] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    duration_days: 30,
    price: 0,
    description: ''
  });

  useEffect(() => {
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      const { data } = await axios.get(`${API}/api/memberships`);
      setMemberships(data);
    } catch (err) {
      console.error('Error fetching memberships:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (membership = null) => {
    if (membership) {
      setEditingMembership(membership);
      setFormData({
        name: membership.name,
        duration_days: membership.duration_days,
        price: membership.price,
        description: membership.description || ''
      });
    } else {
      setEditingMembership(null);
      setFormData({ name: '', duration_days: 30, price: 0, description: '' });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      if (editingMembership) {
        await axios.put(`${API}/api/memberships/${editingMembership.id}`, formData);
        setMemberships(memberships.map(m => m.id === editingMembership.id ? { ...m, ...formData } : m));
        toast.success('Membresía actualizada');
      } else {
        const { data } = await axios.post(`${API}/api/memberships`, formData);
        setMemberships([data, ...memberships]);
        toast.success('Membresía creada');
      }
      setShowModal(false);
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/api/memberships/${deleteId}`);
      setMemberships(memberships.filter(m => m.id !== deleteId));
      toast.success('Membresía eliminada');
    } catch (err) {
      toast.error('Error al eliminar');
    } finally {
      setShowDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const formatDuration = (days) => {
    if (days === 1) return '1 día';
    if (days < 30) return `${days} días`;
    if (days === 30) return '1 mes';
    if (days === 90) return '3 meses';
    if (days === 180) return '6 meses';
    if (days === 365) return '1 año';
    return `${days} días`;
  };

  const cardColors = [
    { bg: 'from-indigo-500/10 to-indigo-600/5', icon: 'bg-indigo-500/15 text-indigo-400', border: 'hover:border-indigo-500/30' },
    { bg: 'from-emerald-500/10 to-emerald-600/5', icon: 'bg-emerald-500/15 text-emerald-400', border: 'hover:border-emerald-500/30' },
    { bg: 'from-amber-500/10 to-amber-600/5', icon: 'bg-amber-500/15 text-amber-400', border: 'hover:border-amber-500/30' },
    { bg: 'from-purple-500/10 to-purple-600/5', icon: 'bg-purple-500/15 text-purple-400', border: 'hover:border-purple-500/30' },
    { bg: 'from-cyan-500/10 to-cyan-600/5', icon: 'bg-cyan-500/15 text-cyan-400', border: 'hover:border-cyan-500/30' },
    { bg: 'from-rose-500/10 to-rose-600/5', icon: 'bg-rose-500/15 text-rose-400', border: 'hover:border-rose-500/30' },
  ];

  return (
    <Layout>
      <motion.div 
        data-testid="memberships-page" 
        className="space-y-8"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Header */}
        <motion.div variants={item} className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-headline text-white">Membresías</h1>
            <p className="text-caption mt-1">{memberships.length} tipos de membresía</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            data-testid="add-membership-button"
            className="nova-btn nova-btn-primary"
          >
            <Plus className="w-4 h-4" />
            Nueva Membresía
          </button>
        </motion.div>

        {/* Memberships Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-52 nova-skeleton rounded-xl" />
            ))}
          </div>
        ) : memberships.length === 0 ? (
          <motion.div variants={item} className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-5">
              <CreditCard className="w-8 h-8 text-indigo-400" />
            </div>
            <p className="text-sm font-medium text-white mb-1">No hay membresías</p>
            <p className="text-caption">Crea tu primera membresía para empezar</p>
            <button
              onClick={() => handleOpenModal()}
              className="mt-5 nova-btn nova-btn-secondary"
            >
              Crear membresía
            </button>
          </motion.div>
        ) : (
          <motion.div variants={container} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {memberships.map((membership, i) => {
              const color = cardColors[i % cardColors.length];
              return (
                <motion.div
                  key={membership.id}
                  variants={item}
                  className={`nova-card nova-card-hover p-6 bg-gradient-to-br ${color.bg} ${color.border} group`}
                >
                  <div className="flex items-start justify-between mb-5">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color.icon}`}>
                      <CreditCard className="w-6 h-6" />
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleOpenModal(membership)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-[var(--nova-text-secondary)]" />
                      </button>
                      <button
                        onClick={() => { setDeleteId(membership.id); setShowDeleteDialog(true); }}
                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                  <h3 className="text-title text-white mb-2">{membership.name}</h3>
                  <p className="nova-stat-value text-3xl mb-2">
                    ${membership.price.toLocaleString()}
                    <span className="text-sm text-[var(--nova-text-tertiary)] font-normal ml-1">MXN</span>
                  </p>
                  <div className="flex items-center gap-2 text-sm text-[var(--nova-text-tertiary)]">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDuration(membership.duration_days)}</span>
                  </div>
                  {membership.description && (
                    <p className="mt-3 text-sm text-[var(--nova-text-quaternary)] line-clamp-2">{membership.description}</p>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Membership Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)] text-[var(--nova-text)] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-title">{editingMembership ? 'Editar Membresía' : 'Nueva Membresía'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="membership-name-input"
                  className="nova-input"
                  placeholder="Ej: Mensual"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Duración (días)</label>
                  <input
                    type="number"
                    value={formData.duration_days}
                    onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 0 })}
                    className="nova-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Precio ($)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    className="nova-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="nova-input h-20 resize-none"
                  placeholder="Descripción opcional..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="nova-btn nova-btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-membership-button"
                  className="nova-btn nova-btn-primary flex-1"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Guardar
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">¿Eliminar membresía?</AlertDialogTitle>
              <AlertDialogDescription className="text-[var(--nova-text-secondary)]">
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="nova-btn nova-btn-secondary">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 text-white hover:bg-red-600 rounded-xl">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </motion.div>
    </Layout>
  );
}
